import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useFolderStore } from '../../store/folderStore';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';

interface RenameFolderForm {
  name: string;
  description?: string;
}

const schema = yup.object().shape({
  name: yup
    .string()
    .required('Folder name is required')
    .max(100, 'Folder name must be less than 100 characters'),
  description: yup
    .string()
    .max(500, 'Description must be less than 500 characters')
    .optional(),
}) as yup.ObjectSchema<RenameFolderForm>;

interface RenameFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId?: string;
  currentName?: string;
  currentDescription?: string;
}

const RenameFolderModal: React.FC<RenameFolderModalProps> = ({
  isOpen,
  onClose,
  folderId,
  currentName,
  currentDescription,
}) => {
  const { updateFolder, isLoading } = useFolderStore();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<RenameFolderForm>({
    resolver: yupResolver(schema),
  });

  // Set current values when modal opens
  React.useEffect(() => {
    if (isOpen && currentName) {
      setValue('name', currentName);
      setValue('description', currentDescription || '');
    }
  }, [isOpen, currentName, currentDescription, setValue]);

  const onSubmit = async (data: RenameFolderForm) => {
    if (!folderId) return;
    
    try {
      await updateFolder(folderId, {
        name: data.name,
        description: data.description,
      });
      handleClose();
    } catch (error) {
      // Error is handled in the store
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!folderId) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Rename Folder"
      maxWidth="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          {...register('name')}
          label="Folder Name"
          placeholder="Enter folder name"
          error={errors.name?.message}
          autoFocus
        />

        <Input
          {...register('description')}
          label="Description (Optional)"
          placeholder="Brief description of this folder"
          error={errors.description?.message}
        />

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isLoading}
            disabled={isLoading}
          >
            Update Folder
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default RenameFolderModal;
